// Referências bibliográficas (texto 14, Partes H, I e K; texto 15, Parte C7): ABNT (padrão) e APA.
// Os dados vêm da própria ficha (livro, filme/série, curso). Campo vazio simplesmente não entra (nunca quebra).
import type { Reg } from '../store/types';
const num = (v: unknown) => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.')); return isFinite(n) ? n : 0; };

export type Estilo = 'abnt' | 'apa';
const s = (v: unknown) => String(v ?? '').trim();
const limpa = (x: string) => x.replace(/\s+/g, ' ').replace(/\s+([.,:;])/g, '$1').replace(/\.\./g, '.').trim();

/** "Machado de Assis; Clarice Lispector" → lista de nomes. Aceita ";" ou " e " / " & " entre os nomes. */
export function autoresDe(txt: string): string[] {
  return s(txt).split(/\s*;\s*|\s+&\s+|\s+e\s+(?=[A-ZÀ-Ú])/).map(x => x.trim()).filter(Boolean);
}
const PARTICULAS = /^(de|da|do|das|dos|e|van|von|der|del|della|di|du|la|le)$/i;
const SUFIXOS = /^(júnior|junior|jr\.?|filho|neto|sobrinho)$/i;
/** Separa sobrenome e prenomes ("Machado de Assis" → ["Assis", "Machado de"]); "Assis, Machado de" já vem separado. */
function partes(nome: string): [string, string] {
  if (nome.includes(',')) { const [a, b] = nome.split(','); return [a.trim(), b.trim()]; }
  const p = nome.trim().split(/\s+/);
  if (p.length === 1) return [p[0], ''];
  let i = p.length - 1;
  if (SUFIXOS.test(p[i]) && i > 0) i--;
  // ABNT: a partícula fica com o prenome ("ASSIS, Machado de")
  return [p.slice(i).join(' '), p.slice(0, i).join(' ')];
}
const iniciais = (prenomes: string) => prenomes.split(/\s+/).filter(x => x && !PARTICULAS.test(x)).map(x => x[0].toUpperCase() + '.').join(' ');
const abntNome = (n: string) => { const [sob, pre] = partes(n); return pre ? `${sob.toUpperCase()}, ${pre}` : sob.toUpperCase(); };
const apaNome = (n: string) => { const [sob, pre] = partes(n); return pre ? `${sob}, ${iniciais(pre)}` : sob; };
/** Sobrenome para a citação: ABNT em maiúsculas, APA normal. */
export function sobrenomeCitacao(n: string, estilo: Estilo) { const sob = partes(n)[0]; return estilo === 'abnt' ? sob.toUpperCase() : sob; }

export const anoDe = (v: unknown) => (s(v).match(/\b(1[5-9]\d\d|20\d\d)\b/)?.[1] ?? '');
const MESES = ['jan.', 'fev.', 'mar.', 'abr.', 'maio', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
const MESES_LONGOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
function dataAcesso(ymd: string, estilo: Estilo) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd); if (!m) return '';
  return estilo === 'abnt' ? `${Number(m[3])} ${MESES[Number(m[2]) - 1]} ${m[1]}` : `${Number(m[3])} de ${MESES_LONGOS[Number(m[2]) - 1]} de ${m[1]}`;
}
const edicao = (v: unknown, estilo: Estilo) => { const n = num(v); if (!n || n <= 1) return s(v) && !n ? s(v) : ''; return `${n}. ed.`; };
const doiUrl = (d: string) => (d ? (/^https?:\/\//i.test(d) ? d : `https://doi.org/${d.replace(/^doi:\s*/i, '')}`) : '');

/** Dados de referência de uma ficha (livro, filme/série, curso), já com os campos que a ficha tem. */
export type DadosRef = { autores: string[]; organizador?: string; tradutor?: string; titulo: string; subtitulo?: string; edicao?: unknown;
  volume?: unknown; editora?: string; cidade?: string; ano?: string; isbn?: string; link?: string; acesso?: string; doi?: string;
  tipo?: 'livro' | 'midia' | 'curso' | 'artigo'; descricao?: string;
  /** texto 21 C5: artigo e página da internet */
  veiculo?: string; numero?: unknown; paginas?: string; data?: string; web?: boolean };

export function dadosDe(r: Reg): DadosRef {
  const c = r.c;
  if (r.col === 'midia') return { tipo: 'midia', autores: autoresDe(c.direcao), titulo: s(c.titulo), editora: s(c.produtora), ano: anoDe(c.ano) || anoDe(c.anoRef),
    link: s(c.link), descricao: s(c.tipo) };
  if (r.col === 'artigo') {
    // texto 21 C5: artigo científico / capítulo (revista, v., n., p.) ou página da internet (site, notícia, blog)
    const tipo = s(c.tipo);
    const web = !!tipo && !['Artigo científico', 'Capítulo de livro'].includes(tipo) && !s(c.doi);
    return { tipo: 'artigo', web, autores: autoresDe(c.autor), titulo: s(c.titulo), subtitulo: s(c.subtitulo), veiculo: s(c.veiculo) || s(c.editora),
      volume: c.volume, numero: c.numero, paginas: s(c.paginasArt), ano: anoDe(c.anoPub), data: s(c.anoPub), link: s(c.linkRef), acesso: s(c.acesso), doi: s(c.doi) };
  }
  if (r.col === 'curso') return { tipo: 'curso', autores: autoresDe(c.instrutor), titulo: s(c.nome), editora: s(c.plataforma), ano: anoDe(c.inicio) || anoDe(c.fim),
    link: s(c.link), acesso: s(c.acesso) };
  return { tipo: 'livro', autores: autoresDe(c.autor), organizador: s(c.organizador), tradutor: s(c.tradutor), titulo: s(c.titulo), subtitulo: s(c.subtitulo),
    edicao: c.edicao, volume: c.volume, editora: s(c.editora), cidade: s(c.cidade), ano: anoDe(c.anoPub) || s(c.anoPub), isbn: s(c.isbn),
    link: s(c.linkRef), acesso: s(c.acesso), doi: s(c.doi) };
}

/** Data de publicação completa ("12/03/2024" ou "2024-03-12"), se houver; senão só o ano. */
function dataPub(v: string | undefined, estilo: Estilo): string {
  const x = s(v); let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(x);
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(x);
  if (br) m = [x, br[3], br[2].padStart(2, '0'), br[1].padStart(2, '0')] as unknown as RegExpExecArray;
  if (!m) return anoDe(x);
  return estilo === 'abnt' ? `${Number(m[3])} ${MESES[Number(m[2]) - 1]} ${m[1]}` : `${m[1]}, ${Number(m[3])} de ${MESES_LONGOS[Number(m[2]) - 1]}`;
}
/** Texto 21 C5: referência de artigo (periódico) ou de página da internet. */
function referenciaArtigo(d: DadosRef, estilo: Estilo): string {
  const titulo = d.subtitulo ? `${d.titulo}: ${d.subtitulo}` : d.titulo;
  const pg = d.paginas ? d.paginas.replace(/\s*[-–]\s*/, '-') : '';
  if (estilo === 'abnt') {
    const resp = d.autores.map(abntNome).join('; ');
    // sem autor, a entrada é pelo título (primeira palavra em maiúsculas)
    const tit = resp ? titulo : titulo.replace(/^(\S+)/, w => w.toUpperCase());
    const partesAbnt = d.web
      ? [resp && resp + '.', tit && tit + '.', d.veiculo && d.veiculo + ',', (dataPub(d.data, 'abnt') || '[s. d.]') + '.',
         d.link && `Disponível em: ${d.link}.` + (d.acesso ? ` Acesso em: ${dataAcesso(d.acesso, 'abnt')}.` : '')]
      : [resp && resp + '.', tit && tit + '.',
         [d.veiculo, num(d.volume) ? `v. ${num(d.volume)}` : '', s(d.numero) ? `n. ${s(d.numero)}` : '', pg ? `p. ${pg}` : '', d.ano || '[s. d.]'].filter(Boolean).join(', ') + '.',
         d.doi && `DOI: ${d.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//i, '')}.`,
         d.link && `Disponível em: ${d.link}.` + (d.acesso ? ` Acesso em: ${dataAcesso(d.acesso, 'abnt')}.` : '')];
    return limpa(partesAbnt.filter(Boolean).join(' ').replace(/,\./g, '.'));
  }
  const nomes = d.autores.map(apaNome);
  const resp = nomes.length > 1 ? nomes.slice(0, -1).join(', ') + ', & ' + nomes[nomes.length - 1] : nomes[0] ?? '';
  const quando = `(${(d.web ? dataPub(d.data, 'apa') : d.ano) || 's.d.'}).`;
  const fonte = d.web ? (d.veiculo ? d.veiculo + '.' : '')
    : [d.veiculo, num(d.volume) ? `${num(d.volume)}${s(d.numero) ? `(${s(d.numero)})` : ''}` : s(d.numero) ? `(${s(d.numero)})` : '', pg.replace('-', '–')].filter(Boolean).join(', ') + '.';
  const onde = d.doi ? doiUrl(d.doi) : d.link ? (d.web && d.acesso ? `Recuperado em ${dataAcesso(d.acesso, 'apa')}, de ${d.link}` : d.link) : '';
  return limpa((resp ? [resp.endsWith('.') ? resp : resp + '.', quando, titulo + '.', fonte, onde] : [titulo + '.', quando, fonte, onde]).filter(x => x && x !== '.').join(' '));
}

/** A referência completa de uma ficha, em ABNT (padrão) ou APA. */
export function referencia(d: DadosRef, estilo: Estilo = 'abnt'): string {
  if (d.tipo === 'artigo') return referenciaArtigo(d, estilo);
  const orgs = d.organizador ? autoresDe(d.organizador) : [];
  if (estilo === 'abnt') {
    const resp = d.autores.length ? d.autores.map(abntNome).join('; ') : orgs.length ? orgs.map(abntNome).join('; ') + ' (org.)' : '';
    const titulo = d.subtitulo ? `${d.titulo}: ${d.subtitulo}` : d.titulo;
    const tipoMidia = d.tipo === 'midia' && d.descricao ? ` [${d.descricao}]` : d.tipo === 'curso' ? ' [Curso]' : '';
    const partesAbnt = [
      resp && resp + '.',
      titulo && titulo + tipoMidia + '.',
      d.tradutor && `Tradução de ${d.tradutor}.`,
      edicao(d.edicao, 'abnt'),
      (() => { const pub = [d.cidade || (d.editora ? '[S. l.]' : ''), d.editora].filter(Boolean).join(': ');
        return pub ? pub + (d.ano ? `, ${d.ano}.` : ', [s. d.].') : d.ano ? `${d.ano}.` : ''; })(),
      num(d.volume) ? `v. ${num(d.volume)}.` : '',
      d.isbn && `ISBN ${d.isbn}.`,
      d.doi && `DOI: ${d.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//i, '')}.`,
      d.link && `Disponível em: ${d.link}.` + (d.acesso ? ` Acesso em: ${dataAcesso(d.acesso, 'abnt')}.` : ''),
    ];
    return limpa(partesAbnt.filter(Boolean).join(' ').replace(/^: ?|(\s): /g, '$1'));
  }
  // APA 7
  const nomes = d.autores.map(apaNome);
  const resp = nomes.length > 1 ? nomes.slice(0, -1).join(', ') + ', & ' + nomes[nomes.length - 1] : nomes[0] ?? (orgs.length ? orgs.map(apaNome).join(', ') + ' (Org.)' : '');
  const titulo = d.subtitulo ? `${d.titulo}: ${d.subtitulo}` : d.titulo;
  const extras = [edicao(d.edicao, 'apa'), num(d.volume) ? `Vol. ${num(d.volume)}` : '', d.tradutor ? `${d.tradutor}, Trad.` : ''].filter(Boolean);
  const tipoMidia = d.tipo === 'midia' && d.descricao ? ` [${d.descricao}]` : d.tipo === 'curso' ? ' [Curso]' : '';
  const tituloApa = titulo && `${titulo}${extras.length ? ` (${extras.join('; ')})` : ''}${tipoMidia}.`;
  const partesApa = [
    resp ? (resp.endsWith('.') ? resp : resp + '.') : tituloApa,   // sem autor, o título vem primeiro
    `(${d.ano || 's.d.'}).`,
    resp ? tituloApa : '',
    d.editora && d.editora + '.',
    d.doi ? doiUrl(d.doi) : d.link ? (d.acesso ? `Recuperado em ${dataAcesso(d.acesso, 'apa')}, de ${d.link}` : d.link) : '',
  ];
  return limpa(partesApa.filter(Boolean).join(' '));
}

/** Citação de um trecho com página: “…” (SOBRENOME, 2020, p. 45) em ABNT ou (Sobrenome, 2020, p. 45) em APA. */
export function citacao(trecho: string, d: DadosRef, pagina: number, estilo: Estilo = 'abnt') {
  const quem = d.autores.length ? (d.autores.length > 3 ? sobrenomeCitacao(d.autores[0], estilo) + ' et al.' : d.autores.map(a => sobrenomeCitacao(a, estilo)).join(estilo === 'abnt' ? '; ' : ' & '))
    : (estilo === 'abnt' ? d.titulo.split(/\s+/)[0]?.toUpperCase() ?? '' : d.titulo);
  const ano = d.ano || (estilo === 'abnt' ? 's. d.' : 's.d.');
  const pag = pagina ? `, p. ${pagina}` : '';
  return `“${s(trecho).replace(/^["“”]+|["“”]+$/g, '')}” (${quem}, ${ano}${pag})`;
}

/** Lista de referências em ordem alfabética (para a pasta). */
export function listaDeReferencias(regs: Reg[], estilo: Estilo = 'abnt') {
  return regs.map(r => referencia(dadosDe(r), estilo)).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** fetch com prazo (15 s): sem isso, um site que não responde deixava o "Buscando..." para sempre (0.77). */
async function comPrazo(url: string, init: RequestInit = {}, ms = 15000): Promise<Response> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const tm = ctl ? setTimeout(() => ctl.abort(), ms) : null;
  try { return await fetch(url, { ...init, ...(ctl ? { signal: ctl.signal } : {}) }); }
  finally { if (tm) clearTimeout(tm); }
}

/** DOI → dados (Crossref, gratuito e público). Devolve só o que encontrar. */
export async function dadosDoDoi(doi: string): Promise<Record<string, any>> {
  const id = doi.trim().replace(/^https?:\/\/(dx\.)?doi\.org\//i, '').replace(/^doi:\s*/i, '');
  if (!/^10\.\d{4,9}\//.test(id)) throw new Error('DOI inválido.');
  const r = await comPrazo(`https://api.crossref.org/works/${encodeURIComponent(id)}`).catch(() => { throw new Error('Não foi possível consultar o DOI agora (sem internet?). Tente de novo.'); });
  if (!r.ok) throw new Error(r.status === 404 ? 'DOI não encontrado.' : `Erro ${r.status} ao consultar o DOI.`);
  const m = (await r.json())?.message ?? {};
  const autores = (m.author ?? []).map((a: any) => [a.given, a.family].filter(Boolean).join(' ')).filter(Boolean);
  const data = m.issued?.['date-parts']?.[0] ?? m.published?.['date-parts']?.[0] ?? [];
  const out: Record<string, any> = { doi: id };
  if (m.title?.[0]) out.titulo = String(m.title[0]);
  if (m.subtitle?.[0]) out.subtitulo = String(m.subtitle[0]);
  if (autores.length) out.autor = autores.join('; ');
  if (m.publisher) out.editora = String(m.publisher);
  if (data[0]) out.anoPub = String(data[0]);
  if (m.URL) out.linkRef = String(m.URL);
  if (m.volume) out.volume = num(m.volume) || '';
  if (m.ISBN?.[0]) out.isbn = String(m.ISBN[0]);
  // texto 21 C6: para artigos, a revista, o número e as páginas
  if (m['container-title']?.[0]) out.veiculo = String(m['container-title'][0]);
  if (m.issue) out.numero = String(m.issue);
  if (m.page) out.paginasArt = String(m.page);
  return out;
}

/** Link de artigo ou site → dados que a própria página informa (etiquetas "citation_*", Open Graph e o título).
 *  Se a página tiver DOI, completa pelo DOI. */
export async function dadosDoLink(link: string): Promise<Record<string, any>> {
  const url = /^https?:\/\//i.test(link) ? link : `https://${link}`;
  let html = '';
  try {
    const r = await comPrazo(url, { headers: { Accept: 'text/html' } });
    if (!r.ok) throw new Error(`Erro ${r.status} ao abrir o link.`);
    html = (await r.text()).slice(0, 400_000);
  } catch (e: any) {
    throw new Error(/Erro \d/.test(String(e?.message)) ? e.message
      : 'O site não deixou ler os dados daqui (no navegador do computador isso é comum). Tente pelo app no celular, use o DOI, ou digite os campos.');
  }
  const metas: [string, string][] = [];
  for (const m of html.matchAll(/<meta\s+[^>]*>/gi)) {
    const tag = m[0];
    const nome = /(?:name|property)\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1]?.toLowerCase();
    const valor = /content\s*=\s*["']([^"']*)["']/i.exec(tag)?.[1];
    if (nome && valor) metas.push([nome, valor.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim()]);
  }
  const um = (...n: string[]) => metas.find(([k]) => n.includes(k))?.[1] ?? '';
  const varios = (n: string) => metas.filter(([k]) => k === n).map(([, v]) => v);
  const out: Record<string, any> = { linkRef: url };
  const titulo = um('citation_title', 'dc.title', 'og:title') || (/<title[^>]*>([^<]+)<\/title>/i.exec(html)?.[1] ?? '').trim();
  if (titulo) out.titulo = titulo;
  const autores = varios('citation_author').length ? varios('citation_author') : [um('author', 'dc.creator', 'article:author')].filter(Boolean);
  if (autores.length) out.autor = autores.map(a => (a.includes(',') ? a.split(',').map(x => x.trim()).reverse().join(' ') : a)).join('; ');
  const ano = anoDe(um('citation_publication_date', 'citation_date', 'dc.date', 'article:published_time'));
  if (ano) out.anoPub = ano;
  const editora = um('citation_publisher', 'citation_journal_title', 'dc.publisher', 'og:site_name');
  if (editora) out.editora = editora;
  const veiculo = um('citation_journal_title', 'og:site_name', 'citation_publisher');
  if (veiculo) out.veiculo = veiculo;
  const dataCompleta = um('citation_publication_date', 'article:published_time', 'citation_date');
  if (/^\d{4}-\d{2}-\d{2}/.test(dataCompleta)) out.dataPub = dataCompleta.slice(0, 10);
  const doi = um('citation_doi', 'dc.identifier');
  if (/^(doi:\s*)?10\.\d{4,9}\//i.test(doi)) {
    try { return { ...out, ...(await dadosDoDoi(doi)), linkRef: url }; } catch { out.doi = doi.replace(/^doi:\s*/i, ''); }
  }
  if (Object.keys(out).length <= 1) throw new Error('A página não informa título nem autor. Digite os campos.');
  return out;
}
